import React, { Component } from 'react';
import PropTypes from 'prop-types';

import OutlinedTextField from '../textFields/OutlinedTextField';
import SectionDivider from '../../shared/SectionDivider';
import AltLoginsForm from '../AltLoginsForm';
import Wrapper from '../Wrapper';
import FormButton from '../FormButton';

import baseStyles from '../../../styles/baseStyles.module.scss';
import formStyles from '../styles/styles.module.scss';
import buttonStyles from '../buttons/styles/styles.module.scss';
import styles from './styles/styles.module.scss';

import { validateUserForm } from '../../../utils/form_validaters/user_form_helper';
import { ServerCommunicationMixin } from '../../../application/javascripts/react_components/mixins/shared/server_communication_mixin';
import { facebookHandler, appleHandler, googleHandler } from '../../../utils/oauth_handlers';
import { ProPageStrings } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportMixin } from '../../../application/javascripts/react_components/mixins/shared/language_support_mixin';

class LoginForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      userEmail: '',
      userPassword: '',
      errors: {
        userEmail: '',
        userPassword: ''
      },
      serverError: null,
      submitting: false,
      showErrorsOnType: false
    };
  }

  static LOGIN_URL = '/api/alltrails/login';
  static FORGOT_PASSWORD_URL = '/users/password/new';
  static SIGN_UP_PAGE_URL = '/signup';
  static TEXT = { ...ProPageStrings.loginText, ...ProPageStrings.loginForm, ...ProPageStrings.formText, ...ProPageStrings.altLoginsText };

  // validations

  failedFormValidation() {
    let validationErrors = { ...this.state.errors };
    let validation;
    for (let name in this.state.errors) {
      validation = validateUserForm(name, this.state[name], validationErrors);
      validationErrors = { ...validationErrors, ...validation.errors };
    }

    this.setState({ errors: validationErrors });
    return this.hasErrors(validationErrors);
  }

  hasErrors(errors) {
    for (let name in errors) {
      if (errors[name].length > 0) {
        return true;
      }
    }
    return false;
  }

  // callbacks

  loginSuccessCallback = data => {
    if (this.props.handleLoginSuccess) {
      return this.props.handleLoginSuccess(data);
    }

    window.location.href = data.welcome_notice
      ? `${this.props.returnTo + (this.props.returnTo.includes('?') ? '&' : '?')}welcome=${data.welcome_notice}`
      : this.props.returnTo;
  };

  loginErrorCallback = err => {
    this.setState({ serverError: err.responseJSON.errors[0].message, submitting: false });
  };

  loginCompleteCallback = () => {};

  // handlers

  handleLogin = e => {
    e.preventDefault();

    if (this.state.submitting) {
      return;
    }

    if (this.failedFormValidation()) {
      this.setState({ showErrorsOnType: true });
      return;
    }

    this.setState({ submitting: true });
    let headers = this.props.locale ? { 'X-Language-Locale': this.props.locale } : {};
    const params = {
      email: this.state.userEmail,
      password: this.state.userPassword,
      create_session: true
    };

    ServerCommunicationMixin.postApiEndpoint(
      LoginForm.LOGIN_URL,
      params,
      this.loginSuccessCallback,
      this.loginErrorCallback,
      this.loginCompleteCallback,
      headers
    );
  };

  handleForgotPasswordClick = () => {
    if (this.props.handleForgotPasswordClick) {
      return this.props.handleForgotPasswordClick();
    }

    window.location = LanguageSupportMixin.wrapUrlSafe(LoginForm.FORGOT_PASSWORD_URL, this.props.locale);
  };

  handleChange = e => {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (this.state.showErrorsOnType) {
      const errors = validateUserForm(name, value, { ...this.state.errors });
      this.setState({ ...errors, [name]: value });
    } else {
      this.setState({ [name]: value });
    }
  };

  handleGoogle = response => {
    googleHandler(response, this.props.locale, this.loginSuccessCallback, () => {}, this.loginCompleteCallback);
  };

  handleFacebook = response => {
    facebookHandler(response, this.props.locale, this.loginSuccessCallback, () => {}, this.loginCompleteCallback);
  };

  handleSignUpClick = () => {
    if (this.props.handleRegisterClick) {
      return this.props.handleRegisterClick();
    }

    window.location.href = LanguageSupportMixin.wrapUrlSafe(LoginForm.SIGN_UP_PAGE_URL, this.props.locale);
  };

  // renders

  renderEmailTextInput() {
    return (
      <div className={`${formStyles.inputItem}`}>
        <OutlinedTextField
          name="userEmail"
          isError={this.state.errors.userEmail.length > 0}
          errorMessage={this.state.errors.userEmail}
          value={this.state.userEmail}
          label={LoginForm.TEXT.email.title}
          changeHandler={this.handleChange}
          enterKeyHandler={this.handleLogin}
          type="email"
        />
      </div>
    );
  }

  renderPasswordInput() {
    return (
      <div className={`${formStyles.inputItem}`}>
        <OutlinedTextField
          name="userPassword"
          isError={this.state.errors.userPassword.length > 0}
          errorMessage={this.state.errors.userPassword}
          value={this.state.userPassword}
          label={LoginForm.TEXT.password.title}
          changeHandler={this.handleChange}
          enterKeyHandler={this.handleLogin}
          type="password"
        />
      </div>
    );
  }

  renderServerError() {
    if (!this.state.serverError) {
      return;
    }

    return <div className={formStyles.serverError}>{this.state.serverError}</div>;
  }

  renderSubmitBtn() {
    return <FormButton value={LoginForm.TEXT.login} disabled={this.state.submitting} />;
  }

  renderForm() {
    return (
      <form onSubmit={this.handleLogin}>
        {this.renderEmailTextInput()}
        {this.renderPasswordInput()}
        {this.renderServerError()}
        {this.renderSubmitBtn()}
      </form>
    );
  }

  renderForgotPasswordLink() {
    return (
      <div className={`${formStyles.action} ${styles.forgotPassword}`}>
        <div className={`${buttonStyles.text}`} onClick={this.handleForgotPasswordClick}>
          {LoginForm.TEXT.forgotPassword}
        </div>
      </div>
    );
  }

  renderFooter() {
    return (
      <>
        <div>
          {LoginForm.TEXT.privacyPolicyLead}{' '}
          <a href="/terms" className={baseStyles.route}>
            {LoginForm.TEXT.termsLink}
          </a>{' '}
          {LoginForm.TEXT.and}{' '}
          <a href="/privacy" className={baseStyles.route}>
            {LoginForm.TEXT.privacy}
          </a>
          .
        </div>
        <div className={`${baseStyles.action}`}>
          {LoginForm.TEXT.noAccount}{' '}
          <span className={`${baseStyles.route} ${baseStyles.route}`} onClick={this.handleSignUpClick}>
            {LoginForm.TEXT.signUpFree}
          </span>
        </div>
      </>
    );
  }

  render() {
    const foot = this.renderFooter();

    return (
      <Wrapper text={LoginForm.TEXT} foot={foot}>
        <div className={baseStyles.section}>
          {this.renderForm()}
          {this.renderForgotPasswordLink()}
        </div>
        <SectionDivider>{LoginForm.TEXT.dividerText}</SectionDivider>
        <AltLoginsForm
          text={LoginForm.TEXT}
          facebookAppId={this.props.facebookAppId}
          googleClientId={this.props.googleClientId}
          googleLoginHandler={this.handleGoogle}
          facebookLoginHandler={this.handleFacebook}
          appleLoginHandler={appleHandler}
        />
      </Wrapper>
    );
  }
}

LoginForm.propTypes = {
  locale: PropTypes.string,
  facebookAppId: PropTypes.number.isRequired,
  googleClientId: PropTypes.string.isRequired,
  returnTo: PropTypes.string, // url
  handleLoginSuccess: PropTypes.func,
  handleRegisterClick: PropTypes.func,
  handleLoginSuccess: PropTypes.func,
  handleForgotPasswordClick: PropTypes.func
};

LoginForm.defaultProps = {
  returnTo: '/'
};

export default LoginForm;
